import React, { useState, useEffect } from 'react'

import {
    Box,
    IconButton,
    TextField,
    Tooltip,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    useTheme,
    FormControl,
    Typography,
    Card,
    CardContent,
    FormHelperText,
    useFormControl,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate } from 'react-router-dom'

import { ContinueButton } from '../../../components/Buttons'
import Dropdown from '../../../components/Dropdown'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
}

function getStyles(styleGuide, selectedStyleGuide, theme) {
    return {
        fontWeight: selectedStyleGuide.includes(styleGuide)
            ? 600
            : 400,
    }
}

function MyFormHelperText() {
    const { filled } = useFormControl() || {};
  
    const helperText = React.useMemo(() => {
      if (filled) {
        return '';
      }
  
      return 'Optional';
    }, [filled]);
  
    return(
        <FormHelperText sx={{ 
                    fontSize: '12px',
                    color: '#6B7280',
                    '&.MuiFormHelperText-root': {
                        marginLeft: '0px',
                        paddingLeft: '0px',
                        color: '#6B7280',
                    }
                }}
            >
                {helperText}
        </FormHelperText>
    )
}

export default function TemplateDetailsComponent({
    templateName,
    handleTemplateNameChange,
    description,
    handleDescriptionChange,
    templateType,
    setTemplateType,
    onNextClick,
    categories,
    styleGuides,
    styleGuide,
    setStyleGuide,
    isDocumentTypeLoading,
}) {
    const theme = useTheme()
    const navigate = useNavigate()

    const handleTemplateTypeChange = (event) => {
        const {
            target: { value },
        } = event
        setTemplateType(value)
    }

    const handleStyleChange = (event) => {
        const {
            target: { value },
        } = event
        setStyleGuide(value)
    }

    const categoryOptions = categories.map((c) => ({
        label: c.label,
        value: c.label,
    }))


    // Removed because styleGuidescontext is not being used and causes rerender
    // const styleGuideOptions = styleGuides.map((c) => ({
    //     label: c.style_guide_name,
    //     value: c.style_guide_name,
    // }))

    const isContinueButtonDisabled = !templateName || !templateType

    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            padding: '40px', 
            paddingTop: '120px', // Account for fixed stepper
            '@media (max-width: 768px)': {
                paddingTop: '100px',
            },
            position: 'relative',
        }}>
            <Box sx={{
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 999,
                width: 'clamp(600px, 60vw, 900px)',
                '@media (max-width: 768px)': {
                    width: 'calc(100vw - 32px)',
                    left: '16px',
                    transform: 'none',
                }
            }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <IconButton
                        onClick={() => navigate('/templates')}
                        sx={{
                            padding: '6px',
                            marginLeft: '-8px',
                            marginTop: '-8px',
                            '&:hover': {
                                backgroundColor: '#F3F4F6',
                            },
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: '20px', color: '#374151' }} />
                    </IconButton>
                    <Box>
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                fontSize: '24px',
                                fontWeight: 600,
                                color: '#111827',
                                mb: 1
                            }}
                        >
                            Create template
                        </Typography>
                        <Typography 
                            sx={{ 
                                fontSize: '14px',
                                color: '#6B7280',
                            }}
                        >
                            Enter template details and configuration
                        </Typography>
                    </Box>
                </Box>

                <Card sx={{
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    padding: '16px',
                    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                    border: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    width: '100%',
                    minWidth: 'unset',
                    '@media (min-width: 769px)': {
                        minWidth: '600px',
                    }
                }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Template Name Field */}
                            <Box>
                                <Typography 
                                    sx={{ 
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: '#374151',
                                        mb: 0.5
                                    }}
                                >
                                    Name
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={templateName}
                                    onChange={handleTemplateNameChange}
                                    placeholder="e.g. Clinical Study Report Template"
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            boxShadow: '0 1px 2px 0 rgb(0, 0, 0, 0.05)',
                                            '& fieldset': {
                                                borderColor: '#E5E7EB',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#D1D5DB',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#1F477D',
                                                borderWidth: '2px',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            padding: '12px 14px',
                                            fontSize: '14px',
                                            color: '#111827',
                                            '&::placeholder': {
                                                color: '#9CA3AF',
                                                opacity: 1,
                                            },
                                        },
                                    }}
                                />
                            </Box>

                            {/* Description Field */}
                            {/* <Box>
                                <Typography 
                                    sx={{ 
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: '#374151',
                                        mb: 0.5
                                    }}
                                >
                                    Description
                                </Typography>
                                <FormControl fullWidth>
                                    <TextField
                                        fullWidth={true}
                                        value={description}
                                        onChange={handleDescriptionChange}
                                        placeholder="e.g. Clinical trial"
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#FFFFFF',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                boxShadow: '0 1px 2px 0 rgb(0, 0, 0, 0.05)',
                                                '& fieldset': {
                                                    borderColor: '#E5E7EB',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#D1D5DB',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#1F477D',
                                                    borderWidth: '2px',
                                                },
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '12px 14px',
                                                fontSize: '14px',
                                                color: '#111827',
                                                '&::placeholder': {
                                                    color: '#9CA3AF',
                                                    opacity: 1,
                                                },
                                            },
                                        }}
                                    />
                                    <MyFormHelperText />
                                </FormControl>
                            </Box> */}

                            {/* Type Field */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: '#374151',
                                        }}
                                    >
                                        Template Type
                                    </Typography>
                                    <Tooltip title="Select the type of document this template will be used for.">
                                        <InfoIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                                    </Tooltip>
                                </Box>
                                <Dropdown
                                    value={templateType}
                                    onChange={handleTemplateTypeChange}
                                    options={categoryOptions}
                                    placeholder="Choose a template type"
                                />
                                {/* <FormControl fullWidth size="small">
                                    <Select
                                        value={templateType}
                                        onChange={handleTemplateTypeChange}
                                        displayEmpty
                                        MenuProps={MenuProps}
                                        sx={{
                                            backgroundColor: '#FFFFFF',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#D1D5DB',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#9CA3AF',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#6366F1',
                                                borderWidth: '1px',
                                            },
                                            '& .MuiSelect-select': {
                                                fontSize: '14px',
                                            },
                                        }}
                                    >
                                        <MenuItem value="" disabled>
                                            <Typography sx={{ color: '#9CA3AF', fontSize: '14px' }}>General</Typography>
                                        </MenuItem>
                                        {categories.map((c) => (
                                            <MenuItem
                                                key={c.label}
                                                value={c.label}
                                                sx={{ fontSize: '14px' }}
                                            >
                                                {c.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl> */}
                            </Box>

                            {/* Style Guide Field */}
                            {/* <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: '#374151',
                                        }}
                                    >
                                        Style guide
                                    </Typography>
                                    <Tooltip title="Choose your organization's style guide.">
                                        <InfoIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                                    </Tooltip>
                                </Box>
                                <Dropdown
                                    value={styleGuide}
                                    onChange={handleStyleChange}
                                    options={styleGuideOptions}
                                    placeholder="Choose a style guide"
                                />
                            </Box> */}
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <ContinueButton
                onClick={onNextClick}
                disabled={isContinueButtonDisabled}
                text="Continue"
                backgroundColor="#1E40AF"
                fontColor="#FFFFFF"
            />
        </Box>
    )
}
